import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import SEOHead from '../components/shared/SEOHead';

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const AboutPage = () => (
  <PageLayout>
    <SEOHead
      title="About Dr Ollie Bell | Kalobrate"
      description="Kalobrate was founded by Dr Ollie Bell, a workplace culture and men's health specialist with a PhD in behaviour change interventions."
      canonical="/about"
    />

    <section className="bg-carbon py-20 md:py-28 relative overflow-hidden">
      <img src="/assets/kalobrate-icon-outline-yellow.png" alt="" aria-hidden="true" className="absolute top-[-10%] right-[-5%] w-[55%] h-[120%] object-contain opacity-[0.06] pointer-events-none select-none" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1
          className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight mb-6"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        >
          About Ollie
        </motion.h1>
      </div>
    </section>

    {/* About Ollie */}
    <section className="py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <motion.div className="lg:col-span-2" {...fadeIn}>
            <img
              src="/assets/ollie-headshot.webp"
              alt="Dr Ollie Bell, founder of Kalobrate"
              className="w-full aspect-[4/5] object-cover object-top corner-cut-br"
            />
          </motion.div>
          <motion.div className="lg:col-span-3" {...fadeIn}>
            <div className="space-y-4 text-base text-carbon/80 leading-relaxed">
              <p><strong className="text-carbon">Dr Ollie Bell</strong> is the founder of Kalobrate and a specialist in workplace culture, men's health and behaviour change.</p>
              <p>His PhD focused on men's health and behaviour change interventions, particularly how to engage groups who are often less likely to take part in traditional health and wellbeing support.</p>
              <p>Ollie has applied that research in manufacturing, engineering, energy, transport, hospitality and other operational workplaces.</p>
              <p>His work includes employee listening, culture reviews, leadership support, workplace learning and facilitated discussions.</p>
              <p>He is known for making difficult subjects clear, practical and relevant to the people in the room.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Why Ollie started Kalobrate */}
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn}>
          <h2 className="text-2xl font-heading font-bold text-carbon mb-6 tracking-tight">Why Ollie started Kalobrate</h2>
          <div className="space-y-4 text-base text-carbon/80 leading-relaxed">
            <p>Ollie started Kalobrate after seeing organisations invest in workplace wellbeing without first understanding the problem they were trying to solve.</p>
            <p>Activities were often introduced with good intentions, but with limited evidence about what employees were experiencing, what was creating pressure or whether the work was making a difference.</p>
            <p>Kalobrate was created to help organisations understand before they act.</p>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Ollie's approach */}
    <section className="py-12 md:py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn}>
          <h2 className="text-2xl font-heading font-bold text-carbon mb-6 tracking-tight">Ollie's approach</h2>
          <div className="space-y-4 text-base text-carbon/80 leading-relaxed">
            <p>Ollie starts by understanding the organisation, its workforce and the pressures surrounding the issue.</p>
            <p>He gathers evidence through methods such as employee surveys, listening sessions, leadership conversations and existing workplace data.</p>
            <p>He then helps leaders identify:</p>
            <ul className="space-y-2 ml-1">
              {['What is working', 'Where pressure is building', 'What may be contributing to it', 'What should be prioritised', 'How progress could be measured'].map(item => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p>Recommendations are based on the realities of the organisation. Ollie does not begin with a fixed programme or assume that more activity is always the answer.</p>
          </div>
        </motion.div>
      </div>
    </section>

    {/* How projects are delivered */}
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn}>
          <h2 className="text-2xl font-heading font-bold text-carbon mb-6 tracking-tight">How projects are delivered</h2>
          <div className="space-y-4 text-base text-carbon/80 leading-relaxed">
            <p>Every Kalobrate project is led by Ollie.</p>
            <p>Where a project requires additional skills, capacity or sector knowledge, Ollie may work with trusted partners or specialists.</p>
            <p>Clients retain one clear lead contact throughout the work.</p>
          </div>
        </motion.div>
      </div>
    </section>

    {/* What clients can expect */}
    <section className="py-12 md:py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn}>
          <h2 className="text-2xl font-heading font-bold text-carbon mb-6 tracking-tight">What clients can expect</h2>
          <p className="text-base text-carbon/80 leading-relaxed mb-6">Clients can expect Ollie to:</p>
          <ul className="space-y-3">
            {[
              'Listen before recommending',
              'Be clear about what the evidence does and does not show',
              'Treat employee information carefully',
              'Make findings understandable',
              'Recommend practical action',
              'Be honest when an activity is unlikely to address the problem',
              'Stay within his professional boundaries',
            ].map(item => (
              <li key={item} className="flex items-start gap-3 text-base text-carbon/80 leading-relaxed">
                <span className="w-1.5 h-1.5 bg-golden rounded-full mt-2.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>

    {/* Speaking and facilitated sessions */}
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeIn}>
          <h2 className="text-2xl font-heading font-bold text-carbon mb-6 tracking-tight">Speaking and facilitated sessions</h2>
          <div className="space-y-4 text-base text-carbon/80 leading-relaxed">
            <p>Ollie also delivers conference talks, leadership discussions, roundtables, workforce sessions and facilitated conversations on workplace culture, men's health and behaviour change.</p>
            <p>These sessions are designed for operational audiences and focus on making difficult subjects practical and accessible.</p>
          </div>
          <div className="mt-6">
            <Link to="/contact" className="text-sm font-semibold text-carbon hover:text-golden transition-colors inline-flex items-center">
              Discuss a speaking enquiry <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-golden py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-carbon mb-4 tracking-tight">Want to talk through a workplace challenge?</h2>
        <p className="text-base text-carbon/80 mb-8 max-w-xl mx-auto leading-relaxed">
          A first conversation is a chance to share what you are seeing and explore whether Kalobrate is the right support.
        </p>
        <Link to="/contact" className="bg-carbon text-white px-7 py-3.5 font-semibold text-sm hover:bg-carbon/90 transition-colors inline-flex items-center">
          Talk to Ollie <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>
    </section>
  </PageLayout>
);

export default AboutPage;
